import React from 'react';

import {RelativeRangeUnits} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import NumericInput from 'ui-library/inputs/NumericInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {NumericInputChangeEvent} from 'util/Types';

export enum RelativeRangeDirection {
  LAST = 'last',
  NEXT = 'next',
}

export interface RelativeDatetimeFilterFormRange {
  direction: RelativeRangeDirection;
  units: RelativeRangeUnits;
  value: number | null;
}

interface RangeDirectionDropdownProps {
  buttonSize: ButtonSize;
  onChangeRangeDirection: (value: RelativeRangeDirection) => void;
  rangeDirection: RelativeRangeDirection;
}

function RangeDirectionDropdown(props: RangeDirectionDropdownProps): JSX.Element {
  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={props.rangeDirection}>
          <Option value={RelativeRangeDirection.LAST} onSelect={props.onChangeRangeDirection}>
            last
          </Option>
          <Option value={RelativeRangeDirection.NEXT} onSelect={props.onChangeRangeDirection}>
            next
          </Option>
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        fullWidth={true}
        isDropdown={true}
        size={props.buttonSize}
        text={props.rangeDirection}
      />
    </Dropdown>
  );
}

interface RangeUnitsDropdownProps {
  buttonSize: ButtonSize;
  onChangeRangeUnits: (RelativeRangeUnits: RelativeRangeUnits) => void;
  rangeUnits: RelativeRangeUnits;
  includeTimeUnits: boolean;
}

function RangeUnitsDropdown(props: RangeUnitsDropdownProps): JSX.Element {
  const units = [
    RelativeRangeUnits.DAYS,
    RelativeRangeUnits.WEEKS,
    RelativeRangeUnits.MONTHS,
    RelativeRangeUnits.YEARS,
  ];
  if (props.includeTimeUnits) {
    units.unshift(RelativeRangeUnits.SECONDS, RelativeRangeUnits.MINUTES, RelativeRangeUnits.HOURS);
  }
  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={props.rangeUnits}>
          {units.map((unit: RelativeRangeUnits) => (
            <Option key={unit} value={unit} onSelect={props.onChangeRangeUnits}>
              {unit}
            </Option>
          ))}
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        fullWidth={true}
        isDropdown={true}
        size={props.buttonSize}
        text={props.rangeUnits}
      />
    </Dropdown>
  );
}

interface RelativeDatetimeFilterFormProps {
  onChangeRange: (range: RelativeDatetimeFilterFormRange) => void;
  range: RelativeDatetimeFilterFormRange;
  includeTimeUnits: boolean;
  buttonSize: ButtonSize;
}

export default function makeRelativeDatetimeFilterFormNodes(
  props: RelativeDatetimeFilterFormProps
): Array<JSX.Element> {
  return [
    <RangeDirectionDropdown
      buttonSize={props.buttonSize}
      onChangeRangeDirection={(direction) => {
        props.onChangeRange({
          ...props.range,
          direction,
        });
      }}
      rangeDirection={props.range.direction}
    />,
    <NumericInput
      onChange={(e: NumericInputChangeEvent) => {
        props.onChangeRange({
          ...props.range,
          value: e.target.numericValue,
        });
      }}
      placeholder="Enter number"
      value={props.range.value}
      validateInteger={true}
      min={0}
    />,
    <RangeUnitsDropdown
      buttonSize={props.buttonSize}
      onChangeRangeUnits={(units) => {
        props.onChangeRange({
          ...props.range,
          units,
        });
      }}
      rangeUnits={props.range.units}
      includeTimeUnits={props.includeTimeUnits}
    />,
  ];
}
